import { NavLink, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Menu, X, LogOut, User } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";

const navItems = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
  { to: "/policies", label: "My Policies", testid: "nav-policies" },
  { to: "/ask", label: "Ask AI", testid: "nav-ask" },
];

const UserMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const initials =
    (user.name || user.email || "?")
      .split(/[\s@]+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-user-btn"
        className="h-9 w-9 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 flex items-center justify-center"
        aria-label="Account"
      >
        {initials}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-2xl bg-white border border-slate-200 shadow-lg overflow-hidden"
          data-testid="nav-user-menu"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-sm font-medium text-slate-900 truncate">
              {user.name || "Signed in"}
            </div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            data-testid="nav-logout"
            className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

export const Navbar = ({ onUploadClick }) => {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const openSignIn = () => {
    setOpen(false);
    setAuthOpen(true);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    navigate("/dashboard");
  };

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-md"
      data-testid="app-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white shadow-sm group-hover:bg-blue-700">
              <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display font-semibold text-slate-900 tracking-tight text-[15px]">
              KnowYourPolicy<span className="text-blue-600">.ai</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() =>
                onUploadClick ? onUploadClick() : navigate("/dashboard")
              }
              data-testid="nav-upload-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full px-5 py-2.5 shadow-sm"
            >
              Upload Policy
            </button>
            {user ? (
              <UserMenu user={user} onLogout={logout} />
            ) : (
              <button
                onClick={openSignIn}
                data-testid="nav-signin"
                className="text-slate-700 hover:text-slate-900 text-sm font-medium rounded-full px-3 py-2 hover:bg-slate-100 inline-flex items-center gap-1.5"
              >
                <User className="h-4 w-4" />
                Sign in
              </button>
            )}
          </div>

          <button
            className="md:hidden p-2 text-slate-700 rounded-lg hover:bg-slate-100"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive
                      ? "text-blue-600 bg-blue-50"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                onUploadClick ? onUploadClick() : navigate("/dashboard");
              }}
              data-testid="nav-upload-btn-mobile"
              className="mt-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full px-5 py-3 shadow-sm"
            >
              Upload Policy
            </button>
            {user ? (
              <button
                onClick={() => {
                  setOpen(false);
                  logout();
                }}
                data-testid="nav-logout-mobile"
                className="mt-1 text-slate-700 rounded-xl px-4 py-3 text-sm font-medium hover:bg-slate-100 text-left inline-flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Log out ({user.email})
              </button>
            ) : (
              <button
                onClick={openSignIn}
                data-testid="nav-signin-mobile"
                className="mt-1 text-slate-700 rounded-xl px-4 py-3 text-sm font-medium hover:bg-slate-100 text-left inline-flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                Sign in
              </button>
            )}
          </div>
        )}
      </div>
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={handleAuthSuccess}
        headline="Welcome to KnowYourPolicy.ai"
        subheadline="Log in or create a free account to unlock Ask AI and see your saved policies across devices."
      />
    </header>
  );
};

export default Navbar;
